import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  IUserTableFieldsFragment,
  PermissionKey,
  UserState,
} from '../../../../../utils/apollo/graphqlSchema';
import { appRoutes } from '../../../../../path';
import GeneralFormItem from '../../../../../components/GeneralFormItem';
import Table from '../../../../../components/Table';
import { activeUsersTableConfig } from './tableStylesConfig';
import * as FormUI from '../../../../pageForm/FormUI';
import ActionsMenu from '../../../../../components/ActionsMenu';
import PermissionElementWrapper from '../../../../PermissionControls/PermissionElementWrapper';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import { ModalSliceType, openModal } from '../../../../../redux/slices/modal';
import { useAppDispatch } from '../../../../../redux/hooks';
import UserStatus from '../UserStatus';
import { useBootstrapSelector } from '../../../../../redux/selectorHooks';
import { assertNotNil } from '../../../../../utils/types';
import {
  defaultViewLabels,
  isDefaultViewMember,
} from '../../../../../utils/userSettings';
import TableDateCell from '../../../../../components/Table/TableDateCell';
import TableUserInfoCell from '../../../../../components/Table/TableUserInfoCell';
import { SingleSelectOption } from '../../../../../components/Dropdown/SingleSelect';
import useCustomerPlan, {
  getMaxSeatsTooltip,
} from '../../../../../utils/hooks/useCustomerPlan';

interface IProps {
  users: IUserTableFieldsFragment[];
  pendingUsersRoute: boolean;
}

const getUserIdUrl = (userId: string) => {
  return `${appRoutes.permission.users}/${userId}`;
};

const columnHelper = createColumnHelper<IUserTableFieldsFragment>();

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: false, // sort by name in ascending order by default
    },
  ] satisfies SortingState,
};

const UsersTable = ({ users, pendingUsersRoute }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const schemaConfig = useBootstrapSelector('schemaConfig');
  assertNotNil(schemaConfig);
  const { schemas } = schemaConfig;

  const { checkIsPermitted } = usePermission();
  const isPermittedToDisableAndEnableUser = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminDelete],
  });

  const { reachMaxSeats, maxSeats } = useCustomerPlan();

  const maxSeatsTooltipContent = getMaxSeatsTooltip(maxSeats);
  const onOpenUserToggleModal = useCallback(
    (user: IUserTableFieldsFragment) => {
      if (!isPermittedToDisableAndEnableUser) {
        return;
      }
      const type =
        user.state === UserState.Active
          ? ModalSliceType.DisableUser
          : ModalSliceType.EnableUser;
      dispatch(openModal({ type, data: { ...user, userId: user.id } }));
    },
    [dispatch, isPermittedToDisableAndEnableUser],
  );
  const handleResendInviteClick = useCallback(
    (user: IUserTableFieldsFragment) => {
      dispatch(
        openModal({ type: ModalSliceType.UserInviteResend, data: user }),
      );
    },
    [dispatch],
  );
  const handleRevokeUserClick = useCallback(
    (user: IUserTableFieldsFragment) => {
      dispatch(openModal({ type: ModalSliceType.RevokeUser, data: user }));
    },
    [dispatch],
  );

  const columns = useMemo(() => {
    const result = [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const name = info.getValue();
          const user = info.row.original;

          if (user.state === UserState.Pending) {
            return <TableUserInfoCell picture={user.picture} name={name} />;
          }

          return (
            <GeneralFormItem
              isDisabled={user.state === UserState.Deleted}
              label={
                <Link to={getUserIdUrl(user.id)}>
                  <TableUserInfoCell picture={user.picture} name={name} />
                </Link>
              }
            />
          );
        },
        meta: activeUsersTableConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('email', {
        id: 'email',
        header: 'Email',
        cell: (info) => {
          const email = info.getValue();
          const user = info.row.original;

          return (
            <FormUI.OverflowingTextWrapper
              $isDisabled={user.state === UserState.Deleted}
            >
              {email}
            </FormUI.OverflowingTextWrapper>
          );
        },
        meta: activeUsersTableConfig.email,
        enableSorting: true,
      }),
      columnHelper.accessor('defaultSchemaSetting', {
        id: 'defaultSchemaSetting',
        header: 'Default Schema',
        cell: (info) => {
          const defaultSchemaSetting = info.getValue();
          const user = info.row.original;

          if (!defaultSchemaSetting?.value) {
            return null;
          }

          const label =
            schemas?.length > 0
              ? schemas.find(
                  (schema) => schema.id === defaultSchemaSetting.value,
                )?.name ?? ''
              : '';

          return (
            <GeneralFormItem
              label={label}
              isDisabled={user.state === UserState.Deleted}
            />
          );
        },
        meta: activeUsersTableConfig.defaultSchema,
      }),
      columnHelper.accessor('defaultViewSetting', {
        id: 'defaultViewSetting',
        header: 'Default View',
        cell: (info) => {
          const defaultViewSetting = info.getValue();
          const user = info.row.original;

          if (
            defaultViewSetting?.value &&
            isDefaultViewMember(defaultViewSetting.value)
          ) {
            return (
              <GeneralFormItem
                label={defaultViewLabels[defaultViewSetting.value]}
                isDisabled={user.state === UserState.Deleted}
              />
            );
          }

          return null;
        },
        meta: activeUsersTableConfig.defaultView,
      }),
      columnHelper.accessor('roles', {
        id: 'roles',
        header: 'Role',
        cell: (info) => {
          const roles = info.getValue();
          const user = info.row.original;

          return (
            <FormUI.OverflowingTextWrapper
              $isDisabled={user.state === UserState.Deleted}
            >
              {roles.map((role) => role.name).join(', ')}
            </FormUI.OverflowingTextWrapper>
          );
        },
        meta: activeUsersTableConfig.roles,
      }),
      columnHelper.accessor('state', {
        id: 'state',
        header: 'Status',
        cell: (info) => {
          const state = info.getValue();

          return <UserStatus state={state} />;
        },
        meta: activeUsersTableConfig.status,
      }),
    ];

    // Pending users page has slightly different columns
    if (pendingUsersRoute) {
      result.push(
        ...[
          columnHelper.accessor('invitedAt', {
            id: 'invitedAt',
            header: 'Invite Date',
            cell: (info) => {
              const date = info.getValue();

              if (!date) {
                return null;
              }

              return <TableDateCell date={date} placement="bottom-end" />;
            },
            meta: activeUsersTableConfig.createdAt,
            enableSorting: true,
          }),
          columnHelper.accessor('inviteExpireAt', {
            id: 'inviteExpireAt',
            header: 'Expiration Date',
            cell: (info) => {
              const date = info.getValue();

              if (!date) {
                return null;
              }

              return <TableDateCell date={date} placement="bottom-end" />;
            },
            meta: activeUsersTableConfig.createdAt,
            enableSorting: true,
          }),
        ],
      );
    } else {
      result.push(
        ...[
          columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Joined On',
            cell: (info) => {
              const date = info.getValue();
              const user = info.row.original;

              if (!date) {
                return null;
              }

              return (
                <TableDateCell
                  date={date}
                  disabled={user.state === UserState.Deleted}
                  placement="bottom-end"
                />
              );
            },
            meta: activeUsersTableConfig.createdAt,
            enableSorting: true,
          }),
          columnHelper.accessor('lastLogin', {
            id: 'lastLogin',
            header: 'Last Login',
            cell: (info) => {
              const date = info.getValue();
              const user = info.row.original;

              if (!date) {
                return null;
              }

              return (
                <TableDateCell
                  date={date}
                  disabled={user.state === UserState.Deleted}
                  placement="bottom-end"
                />
              );
            },
            meta: activeUsersTableConfig.lastLogin,
            enableSorting: true,
          }),
        ],
      );
    }

    return [
      ...result,
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const user = info.row.original;

          return (
            <ActionsMenu>
              {user.state === UserState.Pending ? (
                <>
                  <SingleSelectOption
                    onClick={() => handleResendInviteClick(user)}
                    label={'Resend Invite'}
                  />
                  <SingleSelectOption
                    onClick={() => handleRevokeUserClick(user)}
                    label={'Revoke User'}
                  />
                </>
              ) : (
                <>
                  <SingleSelectOption
                    onClick={() => {
                      navigate(getUserIdUrl(user.id));
                    }}
                    label={'View User'}
                  />
                  <PermissionElementWrapper
                    isPermitted={isPermittedToDisableAndEnableUser}
                    isHalfOpacity={true}
                  >
                    <SingleSelectOption
                      disabled={
                        !isPermittedToDisableAndEnableUser ||
                        // only check the enable button.
                        (reachMaxSeats && user.state === UserState.Deleted)
                      }
                      tooltipOption={{
                        content: maxSeatsTooltipContent,
                        // display tooltip if reachMax and trying to enable the user.
                        // also, not to display 2 tooltips when non-permitted and reachMax.
                        disabled:
                          !isPermittedToDisableAndEnableUser ||
                          !maxSeatsTooltipContent ||
                          !reachMaxSeats ||
                          user.state !== UserState.Deleted,
                      }}
                      onClick={() => {
                        onOpenUserToggleModal(user);
                      }}
                      label={
                        user.state === UserState.Active
                          ? 'Disable User'
                          : 'Enable User'
                      }
                    />
                  </PermissionElementWrapper>
                </>
              )}
            </ActionsMenu>
          );
        },
        meta: activeUsersTableConfig.actions,
      }),
    ];
  }, [
    handleResendInviteClick,
    handleRevokeUserClick,
    isPermittedToDisableAndEnableUser,
    navigate,
    onOpenUserToggleModal,
    pendingUsersRoute,
    reachMaxSeats,
    schemas,
    maxSeatsTooltipContent,
  ]);

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
    />
  );
};

export default UsersTable;
